import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createContext, useContext, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Flex, Heading, IconButton, Dialog as RadixDialog, ScrollArea } from '@radix-ui/themes';
import { cn } from '@/lib/utils';
import { Box } from './Box';
import { Button } from './Button';

export interface DialogTitleProps extends Omit<
  ComponentPropsWithoutRef<typeof Heading>,
  'asChild'
> {
  /** Convenience: renders a back/close button in the start slot when `onBack` is provided.
   *  `false` — no back button (default).
   *  `true` — icon-only back arrow.
   *  `string` — back arrow with the provided label.
   */
  backButton?: boolean | string;
  endSlot?: ReactNode;
}

const isSrOnly = (className?: string) => className?.split(' ').includes('sr-only') ?? false;

function DialogTitle({
  backButton = false,
  endSlot,
  children,
  className,
  ...headingProps
}: DialogTitleProps) {
  const { t } = useTranslation('common');

  const hasBackButton = backButton !== false;
  const closeButton =
    hasBackButton &&
    (backButton === true ? (
      <IconButton type="button" variant="ghost" size="2" aria-label={t('actions.back')}>
        <ChevronLeft strokeWidth={2.5} aria-hidden size={24} />
      </IconButton>
    ) : (
      <Button
        type="button"
        variant="ghost"
        prefixIcon={<ChevronLeft strokeWidth={2.5} aria-hidden size={24} />}
      >
        {backButton}
      </Button>
    ));

  const startSlot = closeButton && <Dialog.Close>{closeButton}</Dialog.Close>;
  if (isSrOnly(className)) {
    return (
      <RadixDialog.Title as="h2" className={className} {...headingProps}>
        {children}
      </RadixDialog.Title>
    );
  }

  return (
    <>
      <RadixDialog.Title as="h2" className={cn('sr-only', 'pt-safe-area')} {...headingProps}>
        {children}
      </RadixDialog.Title>
      <Flex align="center" gap="2" width="100%" pb="3">
        <Flex flexShrink="0" flexBasis="0" justify="start" align="center">
          {startSlot}
        </Flex>

        <Box flexGrow="1">
          <Heading as="h2" size="5" trim="start" className={className} {...headingProps}>
            {children}
          </Heading>
        </Box>

        <Flex flexGrow="1" flexBasis="0" justify="end" align="center">
          {endSlot}
        </Flex>
      </Flex>
    </>
  );
}

type DialogVariant = 'default' | 'fullscreen';

const DialogContext = createContext<DialogVariant>('default');

interface DialogRootProps extends ComponentPropsWithoutRef<typeof RadixDialog.Root> {
  /** When `fullscreen`, the dialog fills the viewport and its children can scroll internally. */
  variant?: DialogVariant;
}

function DialogRoot({ variant = 'default', ...props }: DialogRootProps) {
  return (
    <DialogContext.Provider value={variant}>
      <RadixDialog.Root {...props} />
    </DialogContext.Provider>
  );
}

const DialogContent = ({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof RadixDialog.Content>) => {
  const variant = useContext(DialogContext);
  return (
    <RadixDialog.Content
      className={cn(
        variant === 'fullscreen' && 'pulp-dialog-fullscreen',
        className,
        'pt-safe-area'
      )}
      {...props}
    />
  );
};
DialogContent.displayName = 'Dialog.Content';

function DialogBody(props: ComponentPropsWithoutRef<typeof ScrollArea>) {
  return <ScrollArea scrollbars="vertical" {...props} />;
}
DialogBody.displayName = 'Dialog.Body';

function DialogFooter(props: ComponentPropsWithoutRef<typeof Box>) {
  return <Box flexShrink="0" pt="1" {...props} />;
}
DialogFooter.displayName = 'Dialog.Footer';

export function Dialog(props: DialogRootProps) {
  return <DialogRoot {...props} />;
}

Dialog.Root = DialogRoot;
Dialog.Trigger = RadixDialog.Trigger;
Dialog.Content = DialogContent;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;
Dialog.Title = DialogTitle;
Dialog.Description = RadixDialog.Description;
Dialog.Close = RadixDialog.Close;
